import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Calendar, User, Check, Search, ChevronDown, ChevronUp, Download, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const RawMaterialHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  
  // Filters - default to last 1 month
  const getDefaultDates = () => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return {
      from: oneMonthAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  
  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    product_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRawMaterials();
    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [filters]);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRawMaterials(response.data);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to + 'T23:59:59';
      if (filters.product_id) params.product_id = filters.product_id;
      
      const response = await axios.get(`${API}/raw-material-usage`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalQuantity = (items) => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getGrandTotalQuantity = () => {
    return history.reduce((sum, record) => sum + getTotalQuantity(record.items || []), 0);
  };

  const getSelectedProductName = () => {
    if (!filters.product_id) return null;
    const product = rawMaterials.find(p => p.id === filters.product_id);
    return product?.name;
  };

  const generatePDF = () => {
    if (history.length === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header - Business Name
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(businessInfo?.business_name || 'GPOS', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Raw Material Usage Report', pageWidth / 2, 22, { align: 'center' });

    // Filter Info
    doc.setFontSize(10);
    doc.text(`Period: ${formatDate(filters.from)} to ${formatDate(filters.to)}`, 14, 35);
    if (filters.product_id) {
      doc.text(`Product: ${getSelectedProductName()}`, 14, 42);
    }

    // Summary Box
    doc.setFillColor(245, 245, 245);
    doc.rect(14, 48, pageWidth - 28, 15, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Records: ${history.length}`, 20, 57);
    doc.text(`Total Quantity Used: ${getGrandTotalQuantity().toFixed(2)}`, 100, 57);

    // Build table data - flatten all items from all records
    const tableData = [];
    history.forEach(record => {
      record.items?.forEach((item, idx) => {
        tableData.push([
          idx === 0 ? formatDateTime(record.usage_date) : '',
          item.product_name,
          item.quantity.toFixed(2),
          item.stock_before?.toFixed(2) || '-',
          item.stock_after?.toFixed(2) || '-',
          idx === 0 ? record.used_by : '',
          idx === 0 ? record.verified_by : '',
          item.notes || '-'
        ]);
      });
    });

    doc.autoTable({
      startY: 70,
      head: [['Date', 'Product', 'Qty Used', 'Before', 'After', 'Used By', 'Verified By', 'Notes']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [124, 58, 237], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 30 },
        2: { cellWidth: 18, halign: 'right' },
        3: { cellWidth: 18, halign: 'right' },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 22 },
        6: { cellWidth: 22 },
        7: { cellWidth: 30 }
      },
      margin: { left: 10, right: 10 }
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(9);
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, finalY);

    // Save
    const productSuffix = filters.product_id ? `_${getSelectedProductName()?.replace(/\s+/g, '_')}` : '';
    const fileName = `RawMaterial_Usage${productSuffix}_${filters.from}_to_${filters.to}.pdf`;
    doc.save(fileName);
  };

  const resetFilters = () => {
    setFilters({ ...getDefaultDates(), product_id: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="raw-material-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/gposp/raw-material-usage')}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Usage History</h1>
            <p className="text-slate-500">View raw material usage records</p>
          </div>
        </div>
        {history.length > 0 && (
          <button
            onClick={generatePDF}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            data-testid="download-pdf-btn"
          >
            <Download size={18} />
            Export PDF
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <Filter size={18} />
            Filters
          </button>
          
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Calendar size={16} />
            {formatDate(filters.from)} - {formatDate(filters.to)}
            {filters.product_id && (
              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded">
                {getSelectedProductName()}
              </span>
            )}
          </div>

          <button
            onClick={resetFilters}
            className="text-sm text-violet-600 hover:underline"
          >
            Reset Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Raw Material</label>
              <select
                value={filters.product_id}
                onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Products</option>
                {rawMaterials.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowFilters(false)}
                className="w-full py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {history.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Records</p>
            <p className="text-2xl font-bold text-slate-800">{history.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Qty Used</p>
            <p className="text-2xl font-bold text-violet-600">{getGrandTotalQuantity().toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Period</p>
            <p className="text-lg font-semibold text-slate-700">{formatDate(filters.from)} - {formatDate(filters.to)}</p>
          </div>
        </div>
      )}

      {/* History List */}
      <div className="space-y-4">
        {history.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Package className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-500">No usage records found for the selected filters</p>
          </div>
        ) : (
          history.map((record) => (
            <div 
              key={record.id} 
              className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              data-testid={`history-record-${record.id}`}
            >
              {/* Header - Clickable */}
              <div
                onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                    <Package className="text-violet-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">
                      {record.items?.length || 0} Item{(record.items?.length || 0) !== 1 ? 's' : ''} Used
                    </p>
                    <p className="text-sm text-slate-500">{formatDateTime(record.usage_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-slate-600">
                      <User size={14} className="inline mr-1" />
                      By: {record.used_by}
                    </p>
                    <p className="text-xs text-green-600">
                      <Check size={12} className="inline mr-1" />
                      Verified: {record.verified_by}
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-violet-100 rounded-full">
                    <span className="text-sm font-semibold text-violet-700">
                      {getTotalQuantity(record.items || []).toFixed(2)}
                    </span>
                  </div>
                  {expandedId === record.id ? (
                    <ChevronUp className="text-slate-400" size={20} />
                  ) : (
                    <ChevronDown className="text-slate-400" size={20} />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === record.id && (
                <div className="border-t border-slate-200">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Product</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Qty Used</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Stock Before</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Stock After</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {record.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 font-medium text-sm">{item.product_name}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-violet-600">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-sm text-slate-500">{item.stock_before}</td>
                          <td className="px-4 py-3 text-right text-sm text-slate-500">{item.stock_after}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{item.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td className="px-4 py-2 font-medium text-sm">Total</td>
                        <td className="px-4 py-2 text-right font-bold text-violet-600">{getTotalQuantity(record.items || [])}</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                  {record.overall_notes && (
                    <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                      <p className="text-sm text-amber-800">
                        <strong>Notes:</strong> {record.overall_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RawMaterialHistory;
